import { Award, IndianRupee, Clock, TrendingUp, Percent } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  useDataTable,
  type DataTableColumn,
} from "@/components/shared";
import { useReferralEarnings, useReferralSummary } from "@/hooks/queries";
import type { AccountReferralEarningRow } from "@/types/account.ui";
import { Skeleton } from "@/components/ui/skeleton";

const earningColumns: DataTableColumn<AccountReferralEarningRow>[] = [
  {
    id: "orderNumber",
    header: "Order",
    accessorKey: "orderNumber",
    sortable: true,
    mobilePriority: 1,
    cell: (row) => (
      <div>
        <p className="font-semibold text-sm">{row.orderNumber}</p>
        <p className="text-xs text-muted-foreground">{row.sourceUserName}</p>
      </div>
    ),
  },
  {
    id: "level",
    header: "Level",
    accessorKey: "level",
    sortable: true,
    align: "center",
    mobileLabel: "Level",
    mobilePriority: 2,
    cell: (row) => <Badge variant="outline">L{row.level}</Badge>,
  },
  {
    id: "earnedAmount",
    header: "Earned",
    accessorKey: "earnedAmount",
    sortable: true,
    align: "right",
    mobileLabel: "Earned",
    mobilePriority: 3,
    cell: (row) => <span className="font-bold text-success">₹{row.earnedAmount}</span>,
  },
  {
    id: "isCredited",
    header: "Status",
    accessorKey: "isCredited",
    sortable: true,
    mobileLabel: "Status",
    mobilePriority: 4,
    cell: (row) => (
      <Badge variant="outline" className={row.isCredited ? "bg-success/10 text-success border-success/20" : "bg-warning/10 text-warning border-warning/20"}>
        {row.isCredited ? "credited" : "pending"}
      </Badge>
    ),
  },
  {
    id: "createdAt",
    header: "Date",
    accessorKey: "createdAt",
    hideOnMobile: true,
    className: "text-sm text-muted-foreground",
  },
];

const AccountRewards = () => {
  const { data: summary, isLoading: summaryLoading } = useReferralSummary();
  const { data: earningsData, isLoading: earningsLoading } = useReferralEarnings({ pageSize: 50 });

  const earnings = earningsData?.items ?? [];
  const levelSummary = summary?.levelWiseSummary ?? [];
  const maxLevelEarnings = Math.max(...levelSummary.map((l) => l.totalEarnings), 1);

  const table = useDataTable<AccountReferralEarningRow>({
    data: earnings,
    searchKeys: ["orderNumber", "sourceUserName"],
  });

  if (summaryLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Referral Rewards"
          description="Track level-based referral commissions and earnings."
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Referral Rewards"
        description="Track level-based referral commissions and earnings."
      />

      <StatsGrid>
        <CompactStatCard title="Total Referrals" value={summary?.totalReferrals ?? 0} icon={Award} />
        <CompactStatCard title="Active Referrals" value={summary?.activeReferrals ?? 0} icon={TrendingUp} />
        <CompactStatCard title="Total Earned" value={`₹${summary?.totalEarnings?.toFixed(0) ?? 0}`} icon={IndianRupee} iconClassName="bg-gradient-primary" />
        <CompactStatCard title="Pending" value={`₹${summary?.pendingEarnings?.toFixed(0) ?? 0}`} icon={Clock} />
      </StatsGrid>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Level Performance</CardTitle>
            <CardDescription>Earnings breakdown by referral level</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {levelSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No referral levels configured yet.</p>
            ) : levelSummary.map((level) => (
              <div key={level.level}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">Level {level.level}</span>
                  <span>{level.referralCount} referrals • ₹{level.totalEarnings.toFixed(0)}</span>
                </div>
                <Progress value={Math.min(100, (level.totalEarnings / maxLevelEarnings) * 100)} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{level.commissionPercentage}% commission rate</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Percent className="h-5 w-5" /> Commission Rates</CardTitle>
            <CardDescription>Configured rates per referral level</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {levelSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Admin configures levels in platform settings.</p>
            ) : levelSummary.map((level) => (
              <div key={level.level} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium text-sm">Level {level.level}</p>
                  <Badge variant="outline" className="mt-1">{level.commissionPercentage}%</Badge>
                </div>
                <div className="text-right">
                  <p className="font-bold">₹{level.totalEarnings.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">{level.referralCount} referrals</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {earningsLoading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <DataTable
          title="Recent Commission Earnings"
          columns={earningColumns}
          data={table.rows}
          getRowId={(row) => row.id}
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search orders or users..."
          sort={table.state.sort}
          onSortChange={table.toggleSort}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          emptyMessage="No referral earnings yet. Share your code to start earning!"
          renderMobileCard={(row) => (
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="font-semibold text-sm">{row.orderNumber}</p>
                <p className="text-xs text-muted-foreground">{row.sourceUserName} • Level {row.level} • {row.createdAt}</p>
              </div>
              <span className="text-sm font-bold text-success">₹{row.earnedAmount}</span>
            </div>
          )}
        />
      )}
    </div>
  );
};

export default AccountRewards;
